import { ApiClient } from './ApiClient';
import { ProductDTO } from '../domain/models';
import { mapUnitForApi, mapUnitForDisplay } from '../utils/unitMapping';

export class ProductApiService {
  constructor(private readonly http: ApiClient) {}

  private buildPayload(product: ProductDTO, requireId: boolean): any {
    const unitDisplay = product.unit || 'Stk.'; // Default Display Unit
    const unitApi = mapUnitForApi(unitDisplay) || 'PIECE';
    const payload: any = {
      productKey: product.productKey,
      name: product.name,
      unitPrice: Number(product.unitPrice ?? 0),
      unit: unitApi,
      description: product.description || undefined,
    };
    if (requireId) {
      if (!product.id) throw new Error('id erforderlich');
      payload.id = product.id;
    }
    // Entferne undefined Felder für ein schlankes JSON
    for (const k of Object.keys(payload)) {
      if (payload[k] === undefined) delete payload[k];
    }
    return payload;
  }

  list(): Promise<ProductDTO[]> {
    return this.http.get('/product/all').then(arr => Array.isArray(arr) ? arr.map((p: any) => ({
      id: p.id,
      productKey: p.productKey,
      name: p.name,
      unitPrice: Number(p.unitPrice ?? 0),
      unit: mapUnitForDisplay(p.unit) || 'Stk.',
      description: p.description,
    })) : []);
  }

  create(product: ProductDTO): Promise<ProductDTO> {
    const payload = this.buildPayload(product, false);
    return this.http.post('/product', payload).then((p: any) => ({
      id: p.id,
      productKey: p.productKey,
      name: p.name,
      unitPrice: Number(p.unitPrice ?? 0),
      unit: mapUnitForDisplay(p.unit) || 'Stk.',
      description: p.description,
    }));
  }

  update(product: ProductDTO): Promise<ProductDTO> {
    const payload = this.buildPayload(product, true);
    return this.http.put(`/product/${product.id}`, payload).then((p: any) => ({
      id: p.id,
      productKey: p.productKey,
      name: p.name,
      unitPrice: Number(p.unitPrice ?? 0),
      unit: mapUnitForDisplay(p.unit) || 'Stk.',
      description: p.description,
    }));
  }

  remove(id: number): Promise<void> {
    return this.http.delete(`/product/${id}`);
  }

  removeAll(): Promise<void> {
    return this.http.delete('/product/all');
  }

  findByProductKey(productKey: string): Promise<ProductDTO> {
    const qp = new URLSearchParams({ productKey }).toString();
    return this.http.get(`/product/search-key?${qp}`).then((p: any) => ({
      id: p.id,
      productKey: p.productKey,
      name: p.name,
      unitPrice: Number(p.unitPrice ?? 0),
      unit: mapUnitForDisplay(p.unit) || 'Stk.',
      description: p.description,
    }));
  }

  findByProductName(name: string): Promise<ProductDTO[]> {
    const qp = new URLSearchParams({ name }).toString();
    return this.http.get<unknown>(`/product/search-name?${qp}`).then(value => {
      const arr = Array.isArray(value) ? value : [];
      return arr.map((p: any) => ({
        id: p.id,
        productKey: p.productKey,
        name: p.name,
        unitPrice: Number(p.unitPrice ?? 0),
        unit: mapUnitForDisplay(p.unit) || 'Stk.',
        description: p.description,
      }));
    });
  }
}
